import {IconDeviceGamepad2, IconMinimize, IconTrophy, IconX} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import useFocusTrap from "@/hooks/useFocusTrap";
import useScrollLock from "@/hooks/useScrollLock";
import BetbyWidgetContainer from "../BetbyWidgetContainer";
import GameGridModal from "./GameGridModal";
import GamanzaQuickGamesSidebar from "./GamanzaQuickGamesSidebar";
import styles from "./MultiGameModal.module.css";

const MODAL_TITLE_ID = "multi-game-modal-title";

const MultiGameModal = ({isOpen, onClose, onMinimize, games, onGameSelect}) => {
  const {t} = useTranslation();
  const modalRef = useRef(null);
  const [activeTab, setActiveTab] = useState("casino");
  useFocusTrap(modalRef, isOpen);
  useScrollLock(isOpen);

  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleEscKey = event => {
      if (event.key === "Escape") onClose();
    };

    if (isOpen) document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={MODAL_TITLE_ID}
    >
      <div
        className={styles.modalContainer}
        ref={modalRef}
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <IconDeviceGamepad2
              size={20}
              className={styles.headerIcon}
            />
            <h3
              className={styles.modalTitle}
              id={MODAL_TITLE_ID}
            >
              {t("quickGamesTitle", "Quick Games")}
            </h3>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close modal"
          >
            <IconX size={16} />
          </button>
        </div>

        <GamanzaQuickGamesSidebar active={isOpen} />

        <div className={styles.tabBar}>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === "casino" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("casino")}
          >
            <IconDeviceGamepad2 size={16} />
            {t("casino", "Casino")}
          </button>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === "sports" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("sports")}
          >
            <IconTrophy size={16} />
            {t("sports", "Sports")}
          </button>
        </div>

        <div className={styles.modalContent}>
          {activeTab === "casino" && (
            <GameGridModal
              games={games}
              onGameSelect={onGameSelect}
            />
          )}
          {activeTab === "sports" && (
            <div className={styles.sportsPane}>
              <BetbyWidgetContainer />
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.hideButton}
            onClick={onClose}
          >
            <IconX size={14} />
            {t("hideQuickGames", "Hide")}
          </button>
          <button
            type="button"
            className={styles.minimizeButton}
            onClick={onMinimize}
          >
            <IconMinimize size={14} />
            {t("minimizeQuickGames", "Minimize")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

MultiGameModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func.isRequired,
  games: PropTypes.arrayOf(PropTypes.object).isRequired,
  onGameSelect: PropTypes.func.isRequired,
};

export default MultiGameModal;
